import React from 'react';
import {
  BadgeCheck,
  BookOpen,
  Check,
  CircleOff,
  ImagePlus,
  Lightbulb,
  ListChecks,
  Plus,
  Sparkles,
  Trash2,
} from 'lucide-react';
import { NUTRITION_FIELDS } from '../../data/products';

const inputClass =
  'w-full rounded-xl border border-primary/15 bg-card px-4 py-3 text-sm text-foreground outline-none transition-colors placeholder:text-muted-foreground/55 focus:border-primary/60 focus:ring-2 focus:ring-primary/15';
const labelClass = 'mb-1.5 block text-xs font-medium text-foreground/85';
const hintClass = 'mt-1.5 text-[11px] leading-relaxed text-muted-foreground';

const FOOD_COLOR_PRESETS = [
  '#0F3D2C',
  '#7F9988',
  '#D7B55B',
  '#C97A63',
  '#7D5A78',
  '#355070',
];

const getContrastColor = (color) => {
  const hex = String(color || '').replace('#', '');
  if (hex.length !== 6) return '#FFFFFF';
  const red = Number.parseInt(hex.slice(0, 2), 16);
  const green = Number.parseInt(hex.slice(2, 4), 16);
  const blue = Number.parseInt(hex.slice(4, 6), 16);
  return ((red * 299) + (green * 587) + (blue * 114)) / 1000 > 150
    ? '#0B2F22'
    : '#FFFFFF';
};

function ColorPaletteField({ label, value, onChange, hint }) {
  const normalizedValue = String(value || '').toUpperCase();
  return (
    <div>
      <label className={labelClass}>{label}</label>
      <div className="flex flex-wrap items-center gap-2.5 rounded-xl border border-primary/10 bg-secondary/35 p-3">
        {FOOD_COLOR_PRESETS.map((color) => {
          const selected = normalizedValue === color;
          return (
            <button
              key={color}
              type="button"
              onClick={() => onChange(color)}
              className={`inline-flex h-9 w-9 items-center justify-center rounded-full border-2 transition-transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-primary/25 ${selected ? 'border-foreground shadow-sm' : 'border-card'}`}
              style={{ backgroundColor: color }}
              aria-label={`${color} szín kiválasztása`}
              aria-pressed={selected}
              title={color}
            >
              {selected ? <Check className="h-4 w-4" style={{ color: getContrastColor(color) }} strokeWidth={2.8} /> : null}
            </button>
          );
        })}
        <label className="ml-1 inline-flex cursor-pointer items-center gap-2 rounded-lg border border-primary/10 bg-card px-2.5 py-1.5 text-[11px] font-medium text-foreground/80">
          <input
            type="color"
            value={normalizedValue || '#0F3D2C'}
            onChange={(event) => onChange(event.target.value.toUpperCase())}
            className="h-6 w-6 cursor-pointer rounded border-0 bg-transparent p-0"
            aria-label={`${label} egyéni színe`}
          />
          Egyéni
        </label>
        <span className="text-[11px] font-medium tabular-nums text-muted-foreground">{normalizedValue}</span>
      </div>
      {hint ? <p className={hintClass}>{hint}</p> : null}
    </div>
  );
}

function FoodEditorSection({ number, icon: Icon, title, description, children }) {
  return (
    <section className="rounded-2xl border border-primary/10 bg-card/75 p-5 shadow-sm sm:p-6">
      <div className="mb-5 flex items-start gap-4">
        <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#D7B55B] font-display text-sm font-bold text-[#0B2F22]">
          {number}
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <Icon className="h-4 w-4 text-primary" strokeWidth={1.9} />
            <h3 className="font-display text-lg font-semibold text-primary">{title}</h3>
          </div>
          <p className="mt-1 text-xs leading-relaxed text-muted-foreground">{description}</p>
        </div>
      </div>
      {children}
    </section>
  );
}

export function FoodProductStoryEditor({
  form,
  fileInputId,
  onDetailChange,
  onNutritionChange,
  onCollectionItemChange,
  onAddCollectionItem,
  onRemoveCollectionItem,
  onRecipeImageUpload,
}) {
  const details = form.foodDetails;
  const recipeIdeas = details.recipeIdeas || [];
  const benefits = details.benefits || [];

  return (
    <fieldset className="sm:col-span-2 lg:col-span-12 overflow-hidden rounded-[1.75rem] border border-primary/20 bg-background p-4 shadow-soft sm:p-6">
      <legend className="sr-only">Élelmiszer termékoldal szerkesztése</legend>
      <div className="mb-7 flex flex-col gap-3 border-b border-primary/10 pb-6 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-[#8A6A22]">
            Élelmiszer kategória
          </p>
          <h2 className="mt-2 font-display text-2xl font-semibold text-primary">
            Termékoldal története és struktúrája
          </h2>
          <p className="mt-2 max-w-2xl text-xs leading-relaxed text-muted-foreground">
            A blokkok ebben a sorrendben jelennek meg a vásárlói oldalon. Az üresen hagyott
            tartalmi elemek automatikusan kimaradnak.
          </p>
        </div>
        <span className="inline-flex w-fit items-center gap-2 rounded-full border border-primary/15 bg-primary/[0.06] px-3 py-1.5 text-[11px] font-medium text-primary">
          <Sparkles className="h-3.5 w-3.5" />
          Lumen Verde nézet
        </span>
      </div>

      <div className="space-y-4">
        <FoodEditorSection
          number="01"
          icon={BadgeCheck}
          title="Nyitóblokk és gyors termékadatok"
          description="A név, a rövid és hosszú leírás, az ár és a főfotó a fenti alapadatokból érkezik. Itt add meg a mellettük megjelenő információkat."
        >
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <div>
              <label className={labelClass}>Nettó mennyiség / kiszerelés</label>
              <input
                value={details.netQuantity}
                onChange={(event) => onDetailChange('foodDetails', 'netQuantity', event.target.value)}
                className={inputClass}
                placeholder="pl. 330 g"
              />
            </div>
            <div>
              <label className={labelClass}>Egységár</label>
              <input
                value={details.unitPrice}
                onChange={(event) => onDetailChange('foodDetails', 'unitPrice', event.target.value)}
                className={inputClass}
                placeholder="pl. 5 121 Ft/kg"
              />
            </div>
            <div className="sm:col-span-2">
              <label className={labelClass}>Származási hely</label>
              <input
                value={details.origin}
                onChange={(event) => onDetailChange('foodDetails', 'origin', event.target.value)}
                className={inputClass}
                placeholder="pl. Magyarország / EU és EU-n kívül"
              />
            </div>
            <div className="sm:col-span-2">
              <label className={labelClass}>Étrendi jellemzők (vesszővel)</label>
              <input
                value={details.dietaryTags}
                onChange={(event) => onDetailChange('foodDetails', 'dietaryTags', event.target.value)}
                className={inputClass}
                placeholder="pl. vegán, gluténmentes, pálmaolajmentes"
              />
              <p className={hintClass}>A termék címe alatt, rövid előnylistaként jelenik meg.</p>
            </div>
            <div className="sm:col-span-2">
              <label className={labelClass}>Allergének (vesszővel)</label>
              <input
                value={details.allergens}
                onChange={(event) => onDetailChange('foodDetails', 'allergens', event.target.value)}
                className={inputClass}
                placeholder="pl. földimogyoró, diófélék nyomokban"
              />
            </div>
          </div>
        </FoodEditorSection>

        <FoodEditorSection
          number="02"
          icon={CircleOff}
          title="Összetétel – ami benne van, és ami nincs"
          description="A mockup hangsúlyos összetétel-blokkja. A nagy szám vagy rövid állítás mellett felsorolhatod a tudatosan elhagyott összetevőket."
        >
          <div className="grid gap-4 lg:grid-cols-[0.7fr_1.3fr]">
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-1">
              <div>
                <label className={labelClass}>Kiemelt érték vagy állítás</label>
                <input
                  value={details.ingredientHighlight}
                  onChange={(event) => onDetailChange('foodDetails', 'ingredientHighlight', event.target.value)}
                  className={inputClass}
                  placeholder="pl. 100%"
                />
              </div>
              <div>
                <label className={labelClass}>Kiemelés felirata</label>
                <input
                  value={details.ingredientHighlightLabel}
                  onChange={(event) => onDetailChange('foodDetails', 'ingredientHighlightLabel', event.target.value)}
                  className={inputClass}
                  placeholder="pl. földimogyoró"
                />
              </div>
            </div>
            <div>
              <label className={labelClass}>Nem tartalmazza (vesszővel)</label>
              <textarea
                value={details.excludedIngredients}
                onChange={(event) => onDetailChange('foodDetails', 'excludedIngredients', event.target.value)}
                className={`${inputClass} min-h-28 resize-y`}
                placeholder="pl. hozzáadott cukor, pálmazsír, emulgeálószer, tartósítószer"
              />
              <p className={hintClass}>Minden elem külön, áthúzott kártyaként jelenik meg.</p>
            </div>
          </div>
          <div className="mt-4">
            <ColorPaletteField
              label="Összetétel kiemelőszíne"
              value={details.compositionColor}
              onChange={(color) => onDetailChange('foodDetails', 'compositionColor', color)}
              hint="A kördiagram és a kizárt összetevők jelölőinek színe."
            />
          </div>
        </FoodEditorSection>

        <FoodEditorSection
          number="03"
          icon={ListChecks}
          title="Tápérték, ránézésre"
          description="A legfontosabb értékekből a termékoldal látványos aránysávokat és teljes termékadat-táblát készít."
        >
          <div className="mb-4">
            <ColorPaletteField
              label="Átlátható értékek kiemelőszíne"
              value={details.nutritionColor}
              onChange={(color) => onDetailChange('foodDetails', 'nutritionColor', color)}
              hint="A tápértékblokk hátterét, aránysávjait és kiemelt értékeit hangolja össze."
            />
          </div>
          <div>
            <label className={labelClass}>Tápérték blokk bevezetője</label>
            <textarea
              value={details.nutritionIntro}
              onChange={(event) => onDetailChange('foodDetails', 'nutritionIntro', event.target.value)}
              className={`${inputClass} min-h-20 resize-y`}
              placeholder="Röviden írd le, miért érdekesek a termék tápértékei."
            />
          </div>
          <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
            <div>
              <label htmlFor={`${fileInputId}-nutrition-basis`} className={labelClass}>
                Viszonyítási alap
              </label>
              <select
                id={`${fileInputId}-nutrition-basis`}
                value={form.nutrition.basis}
                onChange={(event) => onNutritionChange('basis', event.target.value)}
                className={inputClass}
              >
                <option className="text-foreground" value="100 g">100 g</option>
                <option className="text-foreground" value="100 ml">100 ml</option>
              </select>
            </div>
            {NUTRITION_FIELDS.map((field) => (
              <div key={field.key}>
                <label htmlFor={`${fileInputId}-nutrition-${field.key}`} className={labelClass}>
                  {field.label}
                </label>
                <div className="relative">
                  <input
                    id={`${fileInputId}-nutrition-${field.key}`}
                    type="text"
                    inputMode="decimal"
                    value={form.nutrition[field.key]}
                    onChange={(event) => onNutritionChange(field.key, event.target.value)}
                    className={`${inputClass} pr-14`}
                    placeholder="–"
                  />
                  <span className="pointer-events-none absolute inset-y-0 right-4 flex items-center text-xs text-muted-foreground">
                    {field.unit}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </FoodEditorSection>

        <FoodEditorSection
          number="04"
          icon={BookOpen}
          title="Felhasználás és receptkártyák"
          description="Adj gyakorlati tanácsot, majd legfeljebb három, saját képpel megjelenő felhasználási vagy receptötletet."
        >
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className={labelClass}>Tálalási javaslat</label>
              <textarea
                value={details.servingSuggestion}
                onChange={(event) => onDetailChange('foodDetails', 'servingSuggestion', event.target.value)}
                className={`${inputClass} min-h-24 resize-y`}
                placeholder="Hogyan érdemes fogyasztani vagy tálalni?"
              />
            </div>
            <div>
              <label className={labelClass}>Adagolás</label>
              <textarea
                value={details.preparation}
                onChange={(event) => onDetailChange('foodDetails', 'preparation', event.target.value)}
                className={`${inputClass} min-h-24 resize-y`}
                placeholder="Ajánlott adag vagy fogyasztási mennyiség"
              />
            </div>
          </div>
          <div className="mt-5 grid gap-4 lg:grid-cols-3">
            {recipeIdeas.map((recipe, index) => {
              const recipeInputId = `${fileInputId}-recipe-${index}`;
              return (
                <div key={`${recipeInputId}-${recipe.image}`} className="rounded-2xl border border-primary/10 bg-secondary/45 p-4">
                  <div className="mb-3 flex items-center justify-between">
                    <span className="text-xs font-semibold text-[#D7B55B]">{index + 1}. kártya</span>
                    <button
                      type="button"
                      onClick={() => onRemoveCollectionItem('recipeIdeas', index)}
                      className="inline-flex h-7 w-7 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-primary/10 hover:text-primary"
                      aria-label={`${index + 1}. receptkártya törlése`}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                  <input
                    id={recipeInputId}
                    type="file"
                    accept="image/*"
                    onChange={(event) => onRecipeImageUpload(event, index)}
                    className="sr-only"
                  />
                  <label htmlFor={recipeInputId} className="group mb-3 block cursor-pointer">
                    <div className="relative aspect-[4/3] overflow-hidden rounded-xl border border-dashed border-primary/20 bg-card">
                      {recipe.image ? (
                        <img src={recipe.image} alt="" className="h-full w-full object-cover" />
                      ) : (
                        <div className="flex h-full flex-col items-center justify-center gap-2 text-muted-foreground">
                          <ImagePlus className="h-5 w-5" />
                          <span className="text-[11px]">Kép feltöltése</span>
                        </div>
                      )}
                      {recipe.image ? (
                        <span className="absolute inset-0 flex items-center justify-center bg-[#0B2F22]/0 text-xs font-medium text-white opacity-0 transition-all group-hover:bg-[#0B2F22]/55 group-hover:opacity-100">
                          Kép cseréje
                        </span>
                      ) : null}
                    </div>
                  </label>
                  <label className={labelClass}>Kártya címe</label>
                  <input
                    value={recipe.title}
                    onChange={(event) => onCollectionItemChange('recipeIdeas', index, 'title', event.target.value)}
                    className={inputClass}
                    placeholder="pl. Ázsiai mártás"
                  />
                </div>
              );
            })}
            {recipeIdeas.length < 3 ? (
              <button
                type="button"
                onClick={() => onAddCollectionItem('recipeIdeas', { title: '', image: '' }, 3)}
                className="flex min-h-56 flex-col items-center justify-center gap-2 rounded-2xl border border-dashed border-primary/25 bg-primary/[0.035] text-primary transition-colors hover:bg-primary/[0.07]"
              >
                <Plus className="h-5 w-5" />
                <span className="text-xs font-medium">Receptkártya hozzáadása</span>
              </button>
            ) : null}
          </div>
        </FoodEditorSection>

        <FoodEditorSection
          number="05"
          icon={Lightbulb}
          title="Előnyök, érdekesség és záró üzenet"
          description="Minden rövid előny mellé adj egy tömör „Tudtad?” magyarázatot. A párok egymás mellett, az újabb előnyök pedig új sorokban jelennek meg."
        >
          <div className="space-y-4">
            {benefits.map((benefit, index) => (
              <div key={`benefit-${index}`} className="rounded-2xl border border-primary/10 bg-secondary/45 p-4">
                <div className="mb-3 flex items-center justify-between">
                  <span className="text-xs font-semibold text-[#D7B55B]">{index + 1}. előny</span>
                  <button
                    type="button"
                    onClick={() => onRemoveCollectionItem('benefits', index)}
                    className="inline-flex h-7 w-7 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-primary/10 hover:text-primary"
                    aria-label={`${index + 1}. előny törlése`}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
                <div className="grid gap-4 md:grid-cols-[0.42fr_0.58fr]">
                  <div>
                    <label className={labelClass}>Rövid előny</label>
                    <input
                      value={benefit.title}
                      onChange={(event) => onCollectionItemChange('benefits', index, 'title', event.target.value)}
                      className={inputClass}
                      placeholder="pl. Sokoldalúan felhasználható"
                    />
                  </div>
                  <div>
                    <label className={labelClass}>„Tudtad?” – rövid magyarázat</label>
                    <textarea
                      value={benefit.description}
                      onChange={(event) => onCollectionItemChange('benefits', index, 'description', event.target.value)}
                      className={`${inputClass} min-h-20 resize-y`}
                      placeholder="Röviden magyarázd el az előnyt."
                    />
                  </div>
                </div>
              </div>
            ))}
            {benefits.length < 4 ? (
              <button
                type="button"
                onClick={() => onAddCollectionItem('benefits', { title: '', description: '' }, 4)}
                className="flex min-h-40 flex-col items-center justify-center gap-2 rounded-2xl border border-dashed border-primary/25 bg-primary/[0.035] text-primary transition-colors hover:bg-primary/[0.07]"
              >
                <Plus className="h-5 w-5" />
                <span className="text-xs font-medium">Előny hozzáadása</span>
              </button>
            ) : null}
          </div>
          <div className="mt-5 grid gap-4 sm:grid-cols-2">
            <div>
              <label className={labelClass}>Záró CTA címe</label>
              <input
                value={details.ctaTitle}
                onChange={(event) => onDetailChange('foodDetails', 'ctaTitle', event.target.value)}
                className={inputClass}
                placeholder="pl. Tiszta összetevők. Végtelen lehetőség."
              />
            </div>
            <div>
              <label className={labelClass}>Záró CTA leírása</label>
              <input
                value={details.ctaText}
                onChange={(event) => onDetailChange('foodDetails', 'ctaText', event.target.value)}
                className={inputClass}
                placeholder="Rövid, meggyőző zárómondat."
              />
            </div>
          </div>
        </FoodEditorSection>
      </div>
    </fieldset>
  );
}
